/* ══════════════════════════════════════════════════════════════
   Market Radar — 10-Signal Composite Visualization
   BUY/HOLD/SELL gauge with individual signal breakdown
   ══════════════════════════════════════════════════════════════ */

import { fetchMLRadar } from '../services/ml-api';
import { renderUnavailableBlock } from './data-state';

export interface RadarSignal {
  name: string;
  value: string;
  signal: 'bullish' | 'bearish' | 'neutral';
  weight: number;
  emoji: string;
}

export interface MarketRadarData {
  score: number;
  signal: string;
  label: string;
  items: RadarSignal[];
}

const SIGNAL_COLORS = {
  bullish: 'var(--india-green)',
  bearish: 'var(--india-red)',
  neutral: 'var(--india-saffron)',
};

function mapApiToRadar(apiData: Record<string, unknown>): MarketRadarData {
  if (apiData.items && Array.isArray(apiData.items)) {
    return {
      score: Number(apiData.score || 0),
      signal: String(apiData.signal || 'HOLD'),
      label: String(apiData.label || `HOLD ${Number(apiData.score || 0)}/10`),
      items: (apiData.items as Array<Record<string, unknown>>).map(item => ({
        name: String(item.name || ''),
        value: String(item.value || ''),
        signal: String(item.signal || 'neutral') as 'bullish' | 'bearish' | 'neutral',
        weight: Number(item.weight || 1),
        emoji: String(item.emoji || '🟡'),
      })),
    };
  }

  // API may return signals array or individual fields
  if (apiData.signals && Array.isArray(apiData.signals)) {
    const signals = apiData.signals as Array<Record<string, unknown>>;
    const items: RadarSignal[] = signals.map(s => {
      const sig = String(s.signal || 'neutral') as 'bullish' | 'bearish' | 'neutral';
      const emoji = sig === 'bullish' ? '🟢' : sig === 'bearish' ? '🔴' : '🟡';
      return {
        name: String(s.name || ''),
        value: String(s.value || ''),
        signal: sig,
        weight: Number(s.weight || 1),
        emoji,
      };
    });
    const score = Number(apiData.score || 5);
    const signal = score >= 7 ? 'BUY' : score >= 4 ? 'HOLD' : 'SELL';
    return { score, signal, label: `${signal} ${score}/10`, items };
  }

  const score = Number(apiData.score || apiData.compositeScore || 0);
  const signal = score >= 7 ? 'BUY' : score >= 4 ? 'HOLD' : 'SELL';
  return { score, signal, label: `${signal} ${score}/10`, items: [] };
}

function renderWithData(container: HTMLElement, d: MarketRadarData): void {
  const gaugeAngle = (d.score / 10) * 180 - 90; // -90 to 90 degrees
  const gaugeColor = d.score >= 7 ? 'var(--india-green)' : d.score >= 4 ? 'var(--india-saffron)' : 'var(--india-red)';

  const signalRows = d.items.map(item => `
    <div class="radar-signal-row">
      <span class="radar-signal-emoji">${item.emoji}</span>
      <span class="radar-signal-name">${item.name}</span>
      <span class="radar-signal-value">${item.value}</span>
      <span class="radar-signal-badge" style="color: ${SIGNAL_COLORS[item.signal]}">${item.signal.toUpperCase()}</span>
    </div>`).join('');

  container.innerHTML = `
    <div class="glass-card market-radar-panel">
      <div class="card-header">
        <h3>📡 Market Radar</h3>
        <span class="radar-score" style="color: ${gaugeColor}">${d.label}</span>
      </div>
      <div class="radar-gauge">
        <div class="gauge-arc">
          <div class="gauge-needle" style="transform: rotate(${gaugeAngle}deg)"></div>
          <div class="gauge-center" style="color: ${gaugeColor}">${d.score}/10</div>
        </div>
        <div class="gauge-labels">
          <span class="sell-label">SELL</span>
          <span class="hold-label">HOLD</span>
          <span class="buy-label">BUY</span>
        </div>
      </div>
      <div class="radar-signals">
        ${signalRows}
      </div>
    </div>`;
}

export function renderMarketRadar(container: HTMLElement, data?: MarketRadarData): void {
  if (data) {
    renderWithData(container, data);
  } else {
    container.innerHTML = `<div class="glass-card market-radar-panel" style="padding:2rem;text-align:center;">Loading market radar...</div>`;
    fetchMLRadar().then(apiData => {
      if (apiData && Object.keys(apiData).length > 0) {
        renderWithData(container, mapApiToRadar(apiData as Record<string, unknown>));
      } else {
        container.innerHTML = renderUnavailableBlock('glass-card market-radar-panel', 'Live market radar unavailable');
      }
    }).catch(() => {
      container.innerHTML = renderUnavailableBlock('glass-card market-radar-panel', 'Live market radar unavailable');
    });
  }
}
